import React from "react";
import WaitlistForm from "./WaitlistForm";
import { PlatformIcon } from "./PlatformIcon";
import { Sparkles, TrendingUp, ArrowUpRight, Wallet } from "lucide-react";
import { useWaitlistCount, socialProofLabel } from "@/hooks/useWaitlistCount";

/* Small floating earning cards layered around the hero phone */
const EarningCard = ({ platform, amount, delta, person, className = "", style, testid }) => (
  <div
    className={`glass rounded-2xl p-4 min-w-[210px] edge-glow-soft float-card ${className}`}
    style={style}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-2">
      {person ? (
        <div
          className="px-2.5 py-1 rounded-lg border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88] font-display font-extrabold text-[12px] leading-none tracking-tight"
          data-testid={`avatar-${person.toLowerCase().replace(/\s/g, "-")}`}
        >
          {person}
        </div>
      ) : (
        <PlatformIcon id={platform} size={32} />
      )}
      <span className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] pulse-dot" />
        live
      </span>
    </div>
    <div className="text-[11px] uppercase tracking-wider text-white/40 font-mono-c">Just deposited</div>
    <div className="text-2xl font-display font-extrabold mt-0.5">${amount}</div>
    <div className="text-xs text-[#00FF88] font-semibold mt-1 flex items-center gap-1">
      <ArrowUpRight className="w-3.5 h-3.5" />
      +{delta}% vs last week
    </div>
  </div>
);

const Chart = () => (
  <svg viewBox="0 0 320 120" className="w-full h-28">
    <defs>
      <linearGradient id="hero-area" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#00FF88" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00FF88" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0,90 C40,80 60,60 90,55 C120,50 140,80 170,70 C200,60 220,30 260,25 C290,22 310,18 320,12 L320,120 L0,120 Z"
      fill="url(#hero-area)"
    />
    <path
      className="draw-line"
      d="M0,90 C40,80 60,60 90,55 C120,50 140,80 170,70 C200,60 220,30 260,25 C290,22 310,18 320,12"
      stroke="#00FF88"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const Hero = () => {
  const count = useWaitlistCount();
  return (
    <section id="top" className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-spotlight">
      <div className="absolute inset-0 bg-grid pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 bg-[#00FF88]/10 border border-[#00FF88]/20 text-[#00FF88] px-3 py-1.5 rounded-full font-mono-c uppercase tracking-[0.18em] text-[10px] font-bold mb-7"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3 h-3" />
            Now in private beta · Built for gig workers
          </div>

          <h1
            className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] font-black tracking-tighter text-white"
            data-testid="hero-title"
          >
            Built for how
            <br />
            gig workers{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-br from-[#00FF88] to-[#14F195] bg-clip-text text-transparent">
                actually get paid.
              </span>
              <span className="absolute -inset-2 bg-[#00FF88]/20 blur-2xl rounded-full -z-0" />
            </span>
          </h1>

          <div className="mt-7 max-w-xl space-y-4" data-testid="hero-subtitle">
            <p className="text-lg lg:text-xl text-white/70 leading-relaxed">
              GigAfloat helps bridge the gap between completed work and platform payouts through{" "}
              <span className="text-white font-semibold">earnings based Floats</span>.
            </p>
            <p className="text-base lg:text-lg text-white/70 leading-relaxed">
              A <strong className="font-bold italic text-white">Float</strong> provides temporary access to funds based on verified earnings activity.
            </p>
            <p className="text-base lg:text-lg text-[#00FF88] font-semibold leading-snug">
              Powered by real earnings, not traditional credit scores or payroll deposits.
            </p>
            <p className="text-sm lg:text-[15px] text-white/55 leading-relaxed">
              Many gig workers receive earnings through platform payouts, digital wallets, and gig worker debit cards instead of traditional payroll deposits. GigAfloat is being built around verified earning activity to better serve today&apos;s gig workforce.
            </p>
          </div>

          <div className="mt-9 max-w-md" data-testid="hero-waitlist-wrapper">
            <WaitlistForm size="lg" testidPrefix="hero-waitlist" />
            <p className="mt-3 text-xs text-white/40 font-mono-c">
              No spam · No credit checks · Cancel anytime
            </p>
            <a
              href="/pilot"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#00FF88] hover:text-white transition-colors group"
              data-testid="hero-pilot-cta"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] pulse-dot" />
              Apply for the Float Pilot
              <span className="group-hover:translate-x-0.5 transition-transform">→</span>
            </a>
            <p className="mt-1 text-[11px] text-white/35 max-w-[28ch] leading-relaxed">
              Limited founder-led pilot. $25 or $50 floats, 3-day or 5-day repayment, no interest.
            </p>
          </div>

          <div className="mt-10 flex items-center gap-5">
            <a
              href="#how"
              className="text-sm text-white/80 hover:text-white border-b border-white/20 hover:border-[#00FF88] pb-0.5 transition-colors font-semibold"
              data-testid="hero-secondary-cta"
            >
              See how it works →
            </a>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {["#00FF88", "#14F195", "#7BE495", "#FFD60A"].map((c, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-[#050505]"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <span className="text-xs text-white/50 font-medium" data-testid="hero-social-proof">
                {count === null ? (
                  "Loading…"
                ) : (
                  <>
                    <span className="text-white font-mono-c">{count.toLocaleString()}</span> earners waiting
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT — visual stage */}
        <div className="lg:col-span-5 relative h-[560px] hidden lg:block">
          {/* Halo */}
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#00FF88]/25 via-[#00FF88]/5 to-transparent blur-3xl" />
          </div>

          {/* Center main card */}
          <div
            className="absolute left-1/2 top-6 -translate-x-1/2 w-[340px] glass-strong rounded-3xl p-6 edge-glow-soft float-card"
            style={{ "--r": "0deg" }}
            data-testid="hero-main-dashboard"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono-c text-white/40 uppercase tracking-widest">
                  Weekly Cash Flow
                </div>
                <div className="text-3xl font-display font-extrabold mt-1">$1,247.50</div>
                <div className="text-xs text-[#00FF88] font-semibold mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" />
                  +18.2% vs avg week
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20 grid place-items-center">
                <Wallet className="w-5 h-5 text-[#00FF88]" />
              </div>
            </div>
            <Chart />
            <div className="mt-3 grid grid-cols-4 gap-2">
              {["doordash", "uber", "lyft", "instacart"].map((p) => (
                <div key={p} className="rounded-xl bg-white/[0.03] border border-white/5 p-2 text-center">
                  <PlatformIcon id={p} size={22} />
                </div>
              ))}
            </div>
          </div>

          {/* Floating cards */}
          <EarningCard
            person="Tenzin B"
            amount="124.50"
            delta="12.4"
            className="absolute -left-6 top-32 delay-1"
            style={{ "--r": "-5deg" }}
            testid="float-card-tenzin"
          />
          <EarningCard
            person="Sacred L"
            amount="86.20"
            delta="8.1"
            className="absolute -right-4 top-72 delay-2"
            style={{ "--r": "4deg" }}
            testid="float-card-sacred"
          />
          <EarningCard
            person="Max Q"
            amount="312.00"
            delta="22.5"
            className="absolute left-2 bottom-12 delay-3"
            style={{ "--r": "-3deg" }}
            testid="float-card-max"
          />

          {/* Float-ready pill */}
          <div
            className="absolute right-0 bottom-32 glass rounded-2xl px-4 py-3 edge-glow float-card delay-2"
            style={{ "--r": "3deg" }}
            data-testid="float-advance-pill"
          >
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88]">
              Float ready
            </div>
            <div className="text-xl font-display font-extrabold">Up to $500</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
